import type { ProfilePieces } from '@/modules/profile/functions/profilePieces';
import type { ProfileSeed } from './profileSeedFromSearch';
import { CENTERS } from '@/common/types/centers';

export type SectionId =
  | 'head'
  | 'booking'
  | 'risman'
  | 'about'
  | 'credentials'
  | 'gallery'
  | 'waiting'
  | 'centers'
  | 'reviews'
  | 'widgets'
  | 'insurance'
  | 'seo'
  | 'recommend';

const settled = (v: unknown) => v !== undefined;

/**
 * A section is "ready" once the pieces it renders from have settled (arrived or failed —
 * `null` counts as settled, `undefined` is still pending). `seed` is the instant-first-frame
 * data carried over from a search-result tap; when present it satisfies `head` on its own.
 *
 * 'waiting' mapping finding (see assembleProfile.ts / sections.tsx): the waiting-time chart
 * reads `feedbacks.statistics` from the ASSEMBLED props, not from `pieces.rateDetails`
 * directly. `feedbacks` (which carries `.statistics`, spread from `rateDetails`) is only put
 * on props when `feedbacksReady` in assembleProfile.ts is true:
 *   feedbacksReady = has('rateDetails') && (has('reviews') || rateDetails?.hide_rates || rateDetails === null)
 * That is the exact same gate `sections.tsx`'s "reviews" card and the waiting-time-statistics
 * card both depend on (the waiting chart's `isShow` reads `feedbacks?.statistics`, which is
 * undefined until `feedbacksReady`). So 'waiting' must depend on precisely the same pieces as
 * 'reviews' — rateDetails AND (unless hidden/failed) reviews — not rateDetails alone.
 */
export function sectionReady(id: SectionId, p: ProfilePieces, seed: ProfileSeed | null): boolean {
  switch (id) {
    case 'head':
      return !!seed || (settled(p.fullName) && settled(p.image) && settled(p.expertise));
    case 'booking': {
      if (!settled(p.centers)) return false;
      const consult = p.centers?.some?.((c: any) => String(c?.id) === CENTERS.CONSULT);
      return !consult || settled(p.onlineServices);
    }
    case 'risman':
      return settled(p.risman);
    case 'about':
      return settled(p.biography);
    case 'credentials':
      return settled(p.fullProfile) && settled(p.expertise);
    case 'gallery':
      return settled(p.gallery);
    case 'waiting':
      return feedbacksReady(p);
    case 'centers':
      return settled(p.centers) && settled(p.centerFacilities);
    case 'reviews':
      return feedbacksReady(p);
    case 'widgets':
      return settled(p.widgets);
    case 'insurance':
      return settled(p.bikoInsurance);
    case 'seo':
      return settled(p.internalLinks);
    case 'recommend':
      return settled(p.centers) && settled(p.expertise);
  }
}

/** feedbacks (reviews card + waiting chart): rate details and, unless hidden/absent, reviews. */
function feedbacksReady(p: ProfilePieces) {
  return settled(p.rateDetails) && (!!p.rateDetails?.hide_rates || p.rateDetails === null || settled(p.reviews));
}

export type RevealState = 'pending' | 'decided';

/** order = DOM order; returns 'decided' only for a ready section with every section before it decided. */
export function revealInOrder(order: SectionId[], ready: (id: SectionId) => boolean): Record<SectionId, RevealState> {
  const out = {} as Record<SectionId, RevealState>;
  let open = true;
  for (const id of order) {
    open = open && ready(id);
    out[id] = open ? 'decided' : 'pending';
  }
  return out;
}
